import { AxiosError } from 'axios';
import { Toast } from '../components/ui/toast';

/**
 * Извлекает понятное сообщение об ошибке из ответа API
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    // Если это Axios ошибка
    if ('response' in error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string; errors?: Array<{ error_message?: string }> }>;
      
      if (axiosError.response) {
        const data = axiosError.response.data;
        
        // Пытаемся извлечь сообщение об ошибке
        if (data?.error) {
          return data.error;
        }
        if (data?.message) {
          return data.message;
        }
        if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          const firstError = data.errors[0];
          return (firstError as any).error_message || (firstError as any).error || 'Произошла ошибка';
        }
        
        // Стандартные HTTP ошибки
        switch (axiosError.response.status) {
          case 400:
            return 'Неверные данные запроса';
          case 401:
            return 'Требуется авторизация';
          case 403:
            return 'Доступ запрещен';
          case 404:
            return 'Ресурс не найден';
          case 500:
            return 'Внутренняя ошибка сервера';
          default:
            return `Ошибка ${axiosError.response.status}`;
        }
      }
      
      // Сетевая ошибка
      if (axiosError.code === 'ECONNABORTED') {
        return 'Превышено время ожидания запроса';
      }
      if (axiosError.code === 'ERR_NETWORK') {
        return 'Ошибка сети. Проверьте подключение к интернету';
      }
    }
    
    return error.message || 'Произошла неизвестная ошибка';
  }
  
  return 'Произошла неизвестная ошибка';
}

/**
 * Проверяет, является ли ошибка сетевой (можно повторить запрос)
 */
export function isRetryableError(error: unknown): boolean {
  if (error instanceof Error && 'code' in error) {
    const axiosError = error as AxiosError;
    // Повторяем только сетевые ошибки и таймауты
    return axiosError.code === 'ECONNABORTED' || axiosError.code === 'ERR_NETWORK';
  }
  return false;
}

/**
 * Выполняет запрос с повторными попытками при сетевых ошибках
 */
export async function retryRequest<T>(
  request: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> {
  let lastError: unknown;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await request();
    } catch (error) {
      lastError = error;
      
      // Если это не повторяемая ошибка или достигнут лимит попыток
      if (!isRetryableError(error) || attempt === maxRetries) {
        throw error;
      }
      
      // Ждем перед следующей попыткой (экспоненциальная задержка)
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt)));
    }
  }
  
  throw lastError;
}

/**
 * Создает Toast уведомление из ошибки
 */
export function createErrorToast(error: unknown, id: string = Date.now().toString()): Toast {
  return {
    id,
    message: getErrorMessage(error),
    type: 'error',
    duration: 7000,
  };
}

/**
 * Создает Toast уведомление об успехе
 */
export function createSuccessToast(message: string, id: string = Date.now().toString()): Toast {
  return {
    id,
    message,
    type: 'success',
    duration: 3000,
  };
}

